import { normalizeSettings } from '../types/workflowSettings';
import { dehydrateNodes } from '../components/workflow/utils/definitionRefs';
/**
 * 캔버스 "저장 상태" 비교용 스냅샷 — 저장/불러오기 시점의 정규화 JSON 과 현재를 비교해 미저장 변경(dirty)을 판정한다.
 * React Flow 가 붙이는 일시적 필드(selected/dragging/measured …)와 캔버스가 파생시키는 data 필드는 비교에서 뺀다.
 */
const NODE_OMIT = new Set(['selected', 'dragging', 'measured', 'positionAbsolute', 'resizing', 'internals', 'zIndex', 'className']);
const EDGE_OMIT = new Set(['selected', 'style', 'animated', 'markerEnd']);
const DATA_OMIT = new Set(['workflowId', 'dfdViolation', 'onInsertNode', 'toolboxDragging', 'dragPosition', 'noInsert', 'parentDragging']);

function cleanData(d: any): any {
  if (!d || typeof d !== 'object') return d;
  const out: any = {};
  Object.keys(d).sort().forEach((k) => { if (!DATA_OMIT.has(k) && typeof d[k] !== 'function') out[k] = d[k]; });
  return out;
}

function round(v: any): any { return typeof v === 'number' ? Math.round(v) : v; }

export function normalizeCanvas(nodes: any[] | undefined, edges: any[] | undefined, settings?: any): string {
  // 정의를 가리키는 데이터 노드는 저장할 때처럼 정의가 주인인 칸을 뺀 모양으로 비교한다
  const ns = dehydrateNodes(nodes).map((n: any) => {
    const o: any = {};
    Object.keys(n).sort().forEach((k) => { if (!NODE_OMIT.has(k)) o[k] = k === 'data' ? cleanData(n[k]) : n[k]; });
    if (o.position) o.position = { x: round(o.position.x), y: round(o.position.y) };
    // 그룹(서브플로/반복그룹)은 자식 배치에 따라 크기를 스스로 바꾸므로(autoSize) 크기 필드는 비교하지 않는다 — 수동 크기(autoSize=false)만 추적
    const autoGroup = ['group', 'subflow', 'repeatGroup'].includes(String(o.type)) && (n.data?.autoSize !== false);
    if (o.style && typeof o.style === 'object') { const st: any = {}; Object.keys(o.style).sort().forEach((k) => { if (autoGroup && (k === 'width' || k === 'height')) return; st[k] = round(o.style[k]); }); o.style = st; }
    if (autoGroup) { delete o.width; delete o.height; }
    if (o.width !== undefined) o.width = round(o.width);
    if (o.height !== undefined) o.height = round(o.height);
    return o;
  }).sort((a, b) => String(a.id).localeCompare(String(b.id)));
  const es = (edges || []).map((e) => {
    const o: any = {};
    Object.keys(e).sort().forEach((k) => { if (!EDGE_OMIT.has(k)) o[k] = k === 'data' ? cleanData(e[k]) : e[k]; });
    return o;
  }).sort((a, b) => String(a.id).localeCompare(String(b.id)));
  return JSON.stringify({ nodes: ns, edges: es, settings: normalizeSettings(settings) });
}

/** 저장 스냅샷과 현재 캔버스가 다른가 (스냅샷이 없으면 캔버스에 무언가 있을 때 dirty) */
export function isCanvasDirty(snapshot: string | undefined, nodes: any[] | undefined, edges: any[] | undefined, settings?: any): boolean {
  const cur = normalizeCanvas(nodes, edges, settings);
  if (snapshot === undefined) return (nodes || []).length > 0 || (edges || []).length > 0;
  return cur !== snapshot;
}
